import { CommonModule, NgIf, NgClass } from '@angular/common';
import { Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogClose, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { LpmCopilotConfig } from 'app/models/lpm.config';
import { DmsFileItem } from 'app/models/dms-file-item';
import { Util } from 'app/models/util';
import { EventService } from 'app/services/event.service';
import { NoteService } from 'app/services/note.service';
import { environment } from 'environments/environment';
import { NgxLoadingModule } from 'ngx-loading';
import { ToastrService } from 'ngx-toastr';

export interface duplicateFileData {
    item: DmsFileItem;
    existingFiles: DmsFileItem[];
    routeFolderId: string;
    copilotConfig: LpmCopilotConfig;
}

@Component({
    selector     : 'modal-duplicate-file',
    templateUrl  : './duplicate-file.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone   : true,
    imports      : [
        MatButtonModule,
        MatIconModule,
        FormsModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        NgIf,
        NgClass,
        NgxLoadingModule,
        MatRadioModule,
        MatDialogClose,
        CommonModule
    ],
})
export class DuplicateFileComponent implements OnInit {
    duplicateFileForm: UntypedFormGroup;
    isLoading = false;
    existFileOptions: 'copy' | 'overwrite' | 'file-specific';

    constructor(
        public matDialogRef: MatDialogRef<DuplicateFileComponent>,
        @Inject(MAT_DIALOG_DATA) public data: duplicateFileData,
        public noteService: NoteService,
        public toastr: ToastrService,
        public eventService: EventService,
        public util: Util,
    ) {}

    ngOnInit(): void {
        if (this.data) {
            this.data.existingFiles.forEach(item => {
                item.isCancel = true;
                // default skip action
                item.some.duplicate_action = 'canceled';
            });
        }
    }

    // true if nothing to upload
    isAllSkipped(): boolean {
        return this.data?.existingFiles?.every(item => item.isCancel) ?? true;
    }

    cancelAll(): void {
        this.onCancel(null, true);
        const files = [...this.data.existingFiles];
        this.resetExistingFileList();
        this.matDialogRef.close(files);
    }

    dup_copy(): void {
        const item = this.data.existingFiles[0];
        this.modifyUrl(item, 'copy');
        this.matDialogRef.close(this.data.existingFiles);
    }

    dup_overwrite(): void {
        const item = this.data.existingFiles[0];
        this.modifyUrl(item, 'replace');
        this.matDialogRef.close(this.data.existingFiles);
    }

    onCancel(file: DmsFileItem = null, all: boolean = false): void {
        this.data.existingFiles.forEach(item => {
            if (item.some.id === file?.some?.id || all) {
                item.isCancel = true;
                item.some.duplicate_action = 'canceled';
            }
        });
    }

    onOptionChange(event: any, item: DmsFileItem): void {
        const value: 'copy' | 'replace' | 'cancel' = event.value;
        switch (value) {
            case 'copy':    this.modifyUrl(item, 'copy');    break;
            case 'replace': this.modifyUrl(item, 'replace'); break;
            default:        this.onCancel(item);             break;
        }
    }

    onOptionChangeForAll(event: any): void {
        const value: 'copy' | 'replace' | 'file-specific' = event.value;
        if (value !== 'file-specific') {
            this.modifyUrl(null, value, true);
        }
    }

    resetExistingFileList(): void {
        this.data.existingFiles = [];
        this.data.item = null;
        this.data.routeFolderId = null;
    }

    modifyUrl(file: DmsFileItem = null, option: 'copy' | 'replace', all: boolean = false): void {
        const url = environment.ROOT_FILE_UPLOAD_URL;
        const queryParams = this.util.getQueryMethod({
            [option]: true,
            matterId: this.data?.copilotConfig?.matterId,
            contactId: this.data?.copilotConfig?.contactIds?.join(',')
        });
        this.data.existingFiles.forEach(item => {
            if (item.some.id === file?.some?.id || all) {
                item.url = `${url}/${this.data.routeFolderId}${queryParams}`;
                item.isCancel = false;
                item.isUploading = false;
                item.isUploaded = false;
                switch (option) {
                    case 'copy':    item.some.duplicate_action = 'duplicated';  break;
                    case 'replace': item.some.duplicate_action = 'overwritten'; break;
                    default:        item.some.duplicate_action = 'canceled';    break;
                }
            }
        });
    }

    uploadFiles(){
       this.matDialogRef.close(this.data.existingFiles);
    }
}
